import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { askVennPanelOpenAtom, askVennPendingMessageAtom } from 'venn-state';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getTextThemeProvider, Icon } from 'venn-ui-kit';
import useReportContext from './useReportContext';
import useChatStream from './useChatStream';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const SUGGESTED_QUESTIONS = [
  'Summarize this report',
  'What factors are driving returns?',
  'How do I add a new block to my report?',
  'What is the Sharpe ratio telling me?',
];

const AskVennPanel = () => {
  const setIsOpen = useSetRecoilState(askVennPanelOpenAtom);
  const [pendingMessage, setPendingMessage] = useRecoilState(askVennPendingMessageAtom);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [inputValue, setInputValue] = useState('');

  const getReportContext = useReportContext();

  const { messages, isStreaming, send, stop, reset } = useChatStream({
    getContext: getReportContext,
  });

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        const viewport = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) {
          viewport.scrollTop = viewport.scrollHeight;
        }
      }
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isStreaming, scrollToBottom]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (pendingMessage) {
      setPendingMessage(undefined);
      send({ message: pendingMessage });
    }
  }, [pendingMessage, setPendingMessage, send]);

  const handleSend = useCallback(
    ({ message }: { message: string }) => {
      const trimmed = message.trim();
      if (!trimmed || isStreaming) return;
      setInputValue('');
      send({ message: trimmed });
    },
    [isStreaming, send],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend({ message: inputValue });
      }
    },
    [handleSend, inputValue],
  );

  return (
    <div
      className="flex flex-col bg-white border-l border-solid border-venn-grey overflow-hidden"
      style={{ gridArea: 'askVenn', height: '100%' }}
    >
      <div className="flex items-center justify-between px-[16px] py-[12px] border-b border-solid border-venn-grey bg-venn-grey-10">
        <div className="flex items-center gap-[8px]">
          <Icon type="sparkles" prefix="fas" style={{ color: '#0072AF', fontSize: 16 }} />
          <span className="text-[16px] font-semibold text-venn-black">{getTextThemeProvider().askAiName}</span>
        </div>
        <div className="flex items-center gap-[4px]">
          <button
            type="button"
            onClick={() => {
              reset();
            }}
            className="flex items-center justify-center w-[28px] h-[28px] rounded-[4px] border-none bg-transparent cursor-pointer hover:bg-venn-grey-20 transition-colors"
            aria-label="New conversation"
          >
            <Icon type="trash-can" prefix="fas" style={{ fontSize: 12, color: '#707A8A' }} />
          </button>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="flex items-center justify-center w-[28px] h-[28px] rounded-[4px] border-none bg-transparent cursor-pointer hover:bg-venn-grey-20 transition-colors"
          >
            <Icon type="xmark" prefix="fas" style={{ fontSize: 14, color: '#707A8A' }} />
          </button>
        </div>
      </div>

      <ScrollArea ref={scrollRef} className="flex-1">
        <div className="p-[16px] flex flex-col gap-[12px]">
          {messages.length === 0 && (
            <div className="flex flex-col gap-[12px] pt-[8px]">
              <div className="text-[14px] text-venn-grey-80 leading-[1.5]">
                Hi! I&apos;m <strong className="text-venn-black">{getTextThemeProvider().askAiName}</strong>, your AI
                assistant. I can help you understand the analysis in your report, explain metrics, and answer questions
                about your portfolios.
              </div>
              <div className="text-[11px] text-venn-grey-60 font-medium mt-[4px] uppercase tracking-wide">
                Suggested
              </div>
              <div className="flex flex-col gap-[6px]">
                {SUGGESTED_QUESTIONS.map((question) => (
                  <button
                    type="button"
                    key={question}
                    onClick={() => handleSend({ message: question })}
                    className="text-left text-[13px] text-venn-dark-blue bg-white border border-solid border-venn-grey-40 rounded-[8px] px-[12px] py-[10px] cursor-pointer hover:bg-venn-grey-10 hover:text-venn-deep-light-blue hover:border-venn-grey-60 transition-colors leading-[1.4] shadow-sm"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                'rounded-[10px] px-[12px] py-[10px] text-[14px] leading-[1.55] max-w-[92%]',
                msg.role === 'user'
                  ? 'bg-venn-dark-blue text-white self-end ml-auto'
                  : 'bg-venn-grey-10 text-venn-black self-start border border-solid border-venn-grey-20',
              )}
            >
              {msg.role === 'assistant' ? (
                <div className="ask-venn-markdown text-[13px] leading-[1.6] [&_p]:m-0 [&_p]:mb-[8px] [&_p:last-child]:mb-0 [&_ul]:m-0 [&_ul]:pl-[16px] [&_ul]:mb-[8px] [&_ol]:m-0 [&_ol]:pl-[16px] [&_ol]:mb-[8px] [&_li]:mb-[3px] [&_strong]:font-semibold [&_h1]:text-[15px] [&_h1]:font-semibold [&_h1]:mb-[8px] [&_h1]:mt-[4px] [&_h2]:text-[14px] [&_h2]:font-semibold [&_h2]:mb-[6px] [&_h2]:mt-[4px] [&_h3]:text-[13px] [&_h3]:font-semibold [&_h3]:mb-[4px] [&_code]:bg-venn-grey-20 [&_code]:px-[4px] [&_code]:py-[1px] [&_code]:rounded-[3px] [&_code]:text-[12px] [&_hr]:border-venn-grey-30 [&_hr]:my-[10px] [&_table]:w-full [&_table]:text-[11px] [&_table]:border-collapse [&_table]:my-[8px] [&_th]:bg-venn-grey-20 [&_th]:px-[8px] [&_th]:py-[5px] [&_th]:text-left [&_th]:font-semibold [&_th]:border [&_th]:border-solid [&_th]:border-venn-grey-30 [&_td]:px-[8px] [&_td]:py-[4px] [&_td]:border [&_td]:border-solid [&_td]:border-venn-grey-30">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                </div>
              ) : (
                msg.content
              )}
            </div>
          ))}

          {isStreaming && messages[messages.length - 1]?.role !== 'assistant' && (
            <div className="bg-venn-grey-10 border border-solid border-venn-grey-20 rounded-[10px] px-[12px] py-[10px] self-start flex gap-[4px] items-center">
              <span className="w-[5px] h-[5px] rounded-full bg-venn-grey-50 animate-pulse" />
              <span className="w-[5px] h-[5px] rounded-full bg-venn-grey-50 animate-pulse [animation-delay:150ms]" />
              <span className="w-[5px] h-[5px] rounded-full bg-venn-grey-50 animate-pulse [animation-delay:300ms]" />
            </div>
          )}
        </div>
      </ScrollArea>

      <div className="p-[12px] bg-white">
        <div className="flex items-center gap-[8px] border border-solid border-venn-grey-40 rounded-[12px] px-[12px] py-[10px] focus-within:border-venn-dark-blue transition-colors">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your report..."
            rows={1}
            className="flex-1 resize-none border-none p-0 text-[14px] font-normal leading-[1.5] outline-none ring-0 shadow-none placeholder:text-venn-grey-50 bg-transparent focus:outline-none focus:ring-0 focus:shadow-none"
            style={{ minHeight: 20, maxHeight: 120 }}
          />
          {isStreaming ? (
            <button
              type="button"
              onClick={stop}
              className="shrink-0 flex items-center justify-center w-[28px] h-[28px] rounded-[6px] bg-venn-black border-none cursor-pointer hover:bg-venn-grey-90 transition-colors"
              aria-label="Stop generating"
            >
              <Icon type="stop" prefix="fas" style={{ fontSize: 10, color: '#ffffff' }} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleSend({ message: inputValue })}
              disabled={!inputValue.trim()}
              className={cn(
                'shrink-0 flex items-center justify-center w-[28px] h-[28px] rounded-[6px] border-none transition-colors',
                inputValue.trim()
                  ? 'bg-venn-dark-blue cursor-pointer hover:bg-venn-deep-dark-blue'
                  : 'bg-venn-grey-30 cursor-not-allowed',
              )}
              aria-label="Send message"
            >
              <Icon type="arrow-up" prefix="fas" style={{ fontSize: 12, color: '#ffffff' }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AskVennPanel;
